import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getCommunityCourses,
  postUseCommunityCourse,
  searchCommunityCatalog,
  type CommunityCourseSummary,
  type CommunitySearchHit,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { CommunitySearchBar } from "./CommunitySearchBar";
import { CourseCard } from "./CourseCard";
import { useUrlState } from "./useUrlState";

type CommunityCourseRow = {
  id: string;
  title: string;
  description: string | null;
  usedInCohortCount: number;
  heroImageUrl?: string | null;
  authorEmail?: string | null;
  authorName?: string | null;
  lessonCount?: number;
};

export default function TeacherCommunityCoursesPage({ embedded = false }: { embedded?: boolean }) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const navigate = useNavigate();
  const [query, setQuery] = useUrlState("q", "");
  const [courses, setCourses] = useState<CommunityCourseSummary[]>([]);
  const [searchHits, setSearchHits] = useState<CommunitySearchHit[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [usingId, setUsingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setError(null);
    setLoading(true);
    try {
      if (query.trim()) {
        const { results } = await searchCommunityCatalog(bearer, { q: query.trim(), limit: 40 });
        setSearchHits(results.filter((r) => r.kind === "course"));
        setCourses([]);
      } else {
        const data = await getCommunityCourses(bearer, { limit: 100, offset: 0 });
        setCourses(data.courses);
        setSearchHits(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load community courses");
    } finally {
      setLoading(false);
    }
  }, [bearer, query]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleUse(courseId: string) {
    if (!bearer || usingId) return;
    setUsingId(courseId);
    setError(null);
    try {
      const { courseId: newId } = await postUseCommunityCourse(bearer, courseId);
      navigate(`/teacher/courses/${newId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not copy course");
    } finally {
      setUsingId(null);
    }
  }

  const rows: CommunityCourseRow[] = searchHits
    ? searchHits.map((h) => ({
        id: h.id,
        title: h.title,
        description: h.description,
        usedInCohortCount: h.usedInCohortCount ?? 0,
        heroImageUrl: h.heroImageUrl,
        authorEmail: h.authorEmail,
        authorName: h.authorName,
      }))
    : courses;

  const body = (
    <>
      <CommunitySearchBar
        value={query}
        onChange={setQuery}
        loading={loading}
        placeholder="Search courses. Try author:teacher@example.com"
      />

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No community courses match your search yet.</p>
      ) : (
        <div className="teacher-course-grid">
          {rows.map((course) => (
            <CourseCard
              key={course.id}
              title={course.title}
              description={course.description}
              heroImageUrl={course.heroImageUrl}
              statsLine={[
                course.lessonCount != null ? `${course.lessonCount} lessons` : null,
                course.authorName ? `by ${course.authorName}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              footerActions={
                <button
                  type="button"
                  className="btn primary tight"
                  disabled={usingId !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleUse(course.id);
                  }}
                >
                  {usingId === course.id ? "Copying…" : "+ My Courses"}
                </button>
              }
            />
          ))}
        </div>
      )}

      {embedded ? null : (
        <p className="muted small" style={{ marginTop: "1rem" }}>
          Share your own from <Link to="/teacher/courses">Courses</Link>.
        </p>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <div className="teacher-page">
      <header className="teacher-page-header">
        <p className="eyebrow">Teacher · Community</p>
        <h1 className="teacher-page-title">Community courses</h1>
        <p className="teacher-page-lede muted">
          Ordered lesson paths you can copy into your courses, then start a cohort when you are ready.
        </p>
      </header>
      {body}
    </div>
  );
}
